import { chapterKeys } from '@/hooks/useChapters';
import type { CardType, PlotCard, PlotLine } from '@/pages/timeline/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { secureTimelineService } from '../supabase/secure-timeline';

// Define cache times - balanced caching to allow updates while preventing excessive refetches
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
// Reasonable stale times that allow for updates when navigating between pages
// Reasonable stale times that allow for updates when navigating between pages
const GC_TIME = DAY_IN_MS * 7; // 7 days garbage collection

// Define query keys for better cache management
export const secureTimelineKeys = {
  all: ['secureTimeline'] as const,
  timeline: (projectId: string) =>
    [...secureTimelineKeys.all, 'timeline', projectId] as const,
  raw: (projectId: string) =>
    [...secureTimelineKeys.all, 'raw', projectId] as const,
};

/**
 * Hook to get the entire timeline state in a single API call
 * Note: This hook ONLY requires projectId parameter (storyId is resolved on the backend)
 */
export function useSecureTimeline(projectId: string) {
  return useQuery({
    queryKey: secureTimelineKeys.timeline(projectId),
    queryFn: () => secureTimelineService.getTimelineState(projectId),
    staleTime: 0, // Always consider data stale to ensure fresh data when navigating
    gcTime: GC_TIME,
    refetchOnMount: true, // Refetch on mount to get latest data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Hook to get the raw timeline data without transformation
 */
export function useRawTimelineData(projectId: string) {
  return useQuery({
    queryKey: secureTimelineKeys.raw(projectId),
    queryFn: () => secureTimelineService.getTimelineData(projectId),
    staleTime: 0, // Always consider data stale to ensure fresh data when navigating
    gcTime: GC_TIME,
    refetchOnMount: true, // Refetch on mount to get latest data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Hook to create a plot card
 */
export function useCreatePlotCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      timelineId,
      plotLineId,
      title,
      content,
      cardType,
      chapterId,
      position,
    }: {
      timelineId: string;
      plotLineId: string;
      title: string;
      content: string;
      cardType: CardType;
      chapterId?: number;
      position?: number;
      projectId: string;
    }) =>
      secureTimelineService.createPlotCard(
        timelineId,
        plotLineId,
        title,
        content,
        cardType,
        chapterId,
        position
      ),
    onSuccess: (_, variables) => {
      console.log('=== Creating plot card:', variables.title);
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to update a plot card
 */
export function useUpdatePlotCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      updates,
      timelineId,
    }: {
      cardId: string;
      updates: Partial<PlotCard>;
      timelineId?: string;
      projectId: string;
    }) =>
      secureTimelineService.updatePlotCard(cardId, updates, timelineId),
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to delete a plot card
 */
export function useDeletePlotCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      timelineId,
    }: {
      cardId: string;
      timelineId: string;
      projectId: string;
    }) =>
      secureTimelineService.deletePlotCard(cardId, timelineId),
    onSuccess: (_, variables) => {
      console.log('=== Deleting plot card:', variables.cardId);
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to create a plot line
 */
export function useCreatePlotLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storyId,
      title,
      color,
    }: {
      storyId: string;
      title: string;
      color: string;
      projectId: string;
    }) =>
      secureTimelineService.createPlotLine(storyId, title, color),
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to update a plot line
 */
export function useUpdatePlotLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lineId,
      updates,
    }: {
      lineId: string;
      updates: Partial<PlotLine>;
      projectId: string;
    }) =>
      secureTimelineService.updatePlotLine(lineId, updates),
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to delete a plot line
 */
export function useDeletePlotLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lineId,
    }: {
      lineId: string;
      projectId: string;
    }) =>
      secureTimelineService.deletePlotLine(lineId),
    onMutate: async ({ lineId, projectId }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: secureTimelineKeys.timeline(projectId) });

      // Snapshot the previous value
      const previousTimeline = queryClient.getQueryData(secureTimelineKeys.timeline(projectId));

      // Optimistically update to the new value
      if (previousTimeline) {
        queryClient.setQueryData(
          secureTimelineKeys.timeline(projectId),
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              plotLines: old.plotLines.filter((line: any) => line.id.toString() !== lineId.toString())
            };
          }
        );
      }

      // Return a context object with the snapshotted value
      return { previousTimeline };
    },
    onError: (err, newTodo, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTimeline) {
        queryClient.setQueryData(
          secureTimelineKeys.timeline(newTodo.projectId),
          context.previousTimeline
        );
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to update timeline settings
 */
export function useUpdateTimelineSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      timelineId,
      updates,
    }: {
      timelineId: string;
      updates: any;
      projectId: string;
    }) =>
      secureTimelineService.updateTimelineSettings(timelineId, updates),
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to move cards to a chapter
 */
export function useMoveCardsToChapter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardIds,
      chapterId,
      timelineId,
    }: {
      cardIds: number[];
      chapterId: number | null;
      timelineId: string;
      projectId: string;
    }) =>
      secureTimelineService.moveCardsToChapter(cardIds, chapterId, timelineId),
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to update the position of a plot card
 */
export function useUpdatePlotCardPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      newPosition,
      timelineId,
      chapterId,
    }: {
      cardId: string;
      newPosition: number;
      timelineId: string;
      chapterId: number | null;
      projectId: string;
    }) =>
      secureTimelineService.updatePlotCardPosition(cardId, newPosition, timelineId, chapterId),
    onSuccess: (_, variables) => {
      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to reorder chapters
 */
export function useReorderChapters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storyId,
      chapterIds,
    }: {
      storyId: string;
      chapterIds: number[];
      projectId: string;
    }) =>
      secureTimelineService.reorderChapters(storyId, chapterIds),
    onSuccess: (data, variables) => {
      // Update the timeline query data with optimistic updates
      const previousData = queryClient.getQueryData(secureTimelineKeys.timeline(variables.projectId));

      if (previousData) {
        // Update the chapters in the timeline state
        queryClient.setQueryData(
          secureTimelineKeys.timeline(variables.projectId),
          (old: any) => {
            if (!old) return old;

            return {
              ...old,
              chapters: data // Use the returned chapters from the mutation
            };
          }
        );
      }

      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });

      // Invalidate the content chapters query used by sidebar and chapter list
      queryClient.invalidateQueries({
        queryKey: chapterKeys.list(variables.storyId)
      });
    },
  });
}

/**
 * Hook to reorder timelines
 */
export function useReorderTimelines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storyId,
      timelineIds,
    }: {
      storyId: string;
      timelineIds: number[];
      projectId: string;
    }) =>
      secureTimelineService.reorderTimelines(storyId, timelineIds),
    onSuccess: (data, variables) => {
      // Update the timeline query data with optimistic updates
      const previousData = queryClient.getQueryData(secureTimelineKeys.timeline(variables.projectId));

      if (previousData) {
        // Update the plotLines in the timeline state
        queryClient.setQueryData(
          secureTimelineKeys.timeline(variables.projectId),
          (old: any) => {
            if (!old) return old;

            return {
              ...old,
              plotLines: data // Use the returned timelines from the mutation
            };
          }
        );
      }

      // Invalidate the secure timeline query to force a refetch
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.timeline(variables.projectId)
      });

      // Also invalidate the raw data query
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}

/**
 * Hook to manually refresh the timeline data
 */
export function useRefreshTimeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
    }: {
      projectId: string;
    }) =>
      secureTimelineService.getTimelineState(projectId),
    onSuccess: (newData, variables) => {
      // Update the timeline query data
      queryClient.setQueryData(
        secureTimelineKeys.timeline(variables.projectId),
        newData
      );

      // Invalidate the raw data
      queryClient.invalidateQueries({
        queryKey: secureTimelineKeys.raw(variables.projectId)
      });
    },
  });
}
